import cookie from 'js-cookie'
import { toast } from 'react-toastify'
import { scroller } from 'react-scroll'
import {
  CHAICE_FASTENER_PLACEMENT_NAME_MAP,
  CHAICE_FASTENER_PLACEMENT_TYPE_MAP,
  CHAICE_FASTENER_SECTION_MAP,
  CHAICE_SHAPE_TYPES_IDS,
} from './constants.js'
import { setCurrentFlow, toggleTypesStepView } from '../actions/layoutActions.js'
import { getPrice, setCanProceedToNextStep, setSelectedType } from '../actions/generalActions.js'
import { APP_URL, FASTENER_PLACEMENT_RECTANGLE, FASTENER_PLACEMENT_SQUARE } from './config.js'
import { ReactComponent as OutlinedError } from '../../assets/icons/outlined-error.svg'
import { ReactComponent as OutlinedCheck } from '../../assets/icons/outlined-check.svg'
import { ReactComponent as OutlinedInfo } from '../../assets/icons/outlined-info.svg'
import { ReactComponent as Close } from '../../assets/icons/close.svg'

export const isEllipsisActive = (element) => {
  return element.offsetWidth < element.scrollWidth
}

/**
 * Creates an error avoiding app crashes in case of Network Errors
 * @param err {Object}
 * @param customMessage {String}
 * @returns {String}
 */
export const createError = (err, customMessage = null) => {
  let message = 'Something went wrong. Please contact technical support. Code 500'
  if (err && (err.statusText || err.message || err.error || err.errors)) {
    message = err.error || err.statusText || err.message || customMessage
  }

  if (process.env.NODE_ENV === 'development') {
    if (err) {
      // eslint-disable-next-line no-console
      console.log(err)
    }
    if (err?.code) {
      // eslint-disable-next-line no-console
      console.log('err.code', err.code)
    }
    if (err?.name) {
      // eslint-disable-next-line no-console
      console.log('err.name', err.name)
    }
  }

  return message
}

/**
 * Sets a cookie
 * @param field {String}
 * @param value {String}
 * @param expires {Date}
 */
export const setCookie = (field, value, expires = null) => {
  cookie.set(field, value, expires ? { expires: expires } : {})
}

/**
 * Retrieves a cookie
 * @param name {String}
 * @returns {String}
 */
export const getCookie = (name = null) => cookie.get(name)

/**
 * Deletes a cookie
 * @param field {String}
 */
export const removeCookie = (field) => {
  cookie.remove(field)
}

/**
 * Transforms string into a slug
 * @param str
 * @return {string}
 */
export const slugify = (str) => {
  str = str.replace(/^\s+|\s+$/g, '') // trim
  str = str.toLowerCase()

  // remove accents, swap ñ for n, etc
  let from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;'
  let to = 'aaaaeeeeiiiioooouuuunc------'
  for (let i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i))
  }

  str = str
    .replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-') // collapse dashes

  return str
}

/**
 * Add canonical link to head
 * @param url
 * @return {null}
 */
export const addCanonical = (url) => {
  let canonicalLink = document.querySelector('link[rel="canonical"]')

  if (!canonicalLink) {
    canonicalLink = document.createElement('link')
    canonicalLink.setAttribute('rel', 'canonical')
    document.querySelector('head').appendChild(canonicalLink)
  }

  canonicalLink.setAttribute('href', url)
}

/**
 * Select description for current step
 * @param attributes array of attributes for a product
 * @param step curent cushion designer step
 * @returns {Object}
 */
export const selectDescriptions = (attributes, step) => {
  const metaTitle = attributes
    ?.find((attribute) => attribute.name === `Step${step}_Meta_Title`)
    ?.options?.join(' ')
  const metaDescription = attributes
    ?.find((attribute) => attribute.name === `Step${step}_Meta_Description`)
    ?.options?.join(' ')
  const htmlTitle = attributes?.find(
    (attribute) => attribute.name === `Step${step}_Html_Title`,
  )?.options
  const htmlDescription = attributes
    ?.find((attribute) => attribute.name === `Step${step}_Html_Description`)
    ?.options?.map((description) => description.trim())

  return {
    metaTitle,
    metaDescription,
    htmlTitle: htmlTitle?.length > 1 ? [htmlTitle[0], htmlTitle.slice(1).join(' ')] : htmlTitle,
    htmlDescription,
  }
}

/**
 * adds params to string
 * @param url
 * @param params
 * merges multiple parameterwith param.name= prefix_name
 * @param prefix
 * @returns {string}
 */
export const setURLParams = (url, params, prefixToRemove) => {
  const searchParams = new URLSearchParams(url)
  const prefixToRemoveUnderscore = `${prefixToRemove}_`

  params.forEach((param) => {
    let paramName = param.name

    if (param.name.includes(prefixToRemoveUnderscore)) {
      paramName = param.name.replace(prefixToRemoveUnderscore, '')
    }

    searchParams.set(paramName, param.value)
  })
  return searchParams.toString()
}

/**
 * Capitalizes a word
 * @param word {String}
 * @return {String}
 */
export const capitalizeWord = (word) => word?.charAt(0)?.toUpperCase() + word?.slice(1)

/**
 * Capitalizes a string
 * @param word {String}
 * @return {String}
 */
export const capitalizeString = (string, breakChar = ' ') =>
  string
    .split(breakChar)
    .map((word) => capitalizeWord(word))
    .join(breakChar)

/**
 * Displays an error toast with a message
 * @param message {String}
 * @param type {'error'|'success'|'info'}
 * @return {String}
 */
export const displayToast = (message, type) => {
  let icon

  switch (type) {
    case 'error':
      icon = <OutlinedError />
      break
    case 'info':
      icon = <OutlinedInfo />
      break
    case 'success':
      icon = <OutlinedCheck />
      break
  }

  toast(message, {
    hideProgressBar: true,
    progress: undefined,
    autoClose: 5000,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    type: type,
    icon,
    closeButton: <Close />,
    className: 'toaster-component',
  })
}

/**
 * Displays an async toast with a message
 * @param promise Promise
 * @param message Object
 * @param message.success String
 * @param message.pending String
 * @param message.error String
 * @return undefined
 */
export const displayPromiseToast = (promise = null, messages = {}) => {
  toast.promise(promise, {
    success: {
      render() {
        return messages.success
      },
      icon: <OutlinedCheck />,
    },
    error: {
      render() {
        return messages.error
      },
      icon: <OutlinedError />,
    },
    pending: {
      render() {
        return messages.pending
      },
      type: 'success',
    },
    toastOptions: {
      hideProgressBar: true,
      progress: undefined,
      autoClose: 5000,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      closeButton: <Close />,
      className: 'toaster-component',
    },
  })
}

/**
 * Redirects to a specific route
 * @param location {String}
 * @param history {Object}
 * @return {String}
 */
export const redirectTo = (location, history) => history.push(location)

// Helper constant for filters
const NAME_FILTER_MAP = {
  Color: 'colorMap',
  // stock???
  // price???
  brand: 'brands',
  material: 'materials',
  use: 'uses',
  pattern: 'patterns',
}

const NAME_LOCAL_FILTER_MAP = {
  Color: 'color',
  // stock???
  // price???
  brand: 'brand',
  material: 'material',
  use: 'use',
  pattern: 'pattern',
}

export const updateFilterResponse = (fabricsRespons, fabrics) => {
  fabricsRespons?.forEach((fabric) => {
    fabric.filterOptions = {}
    fabric.attributes?.forEach((attribute) => {
      const nameFilterValue = NAME_FILTER_MAP[attribute.name]
      const nameLocalFilterValue = NAME_LOCAL_FILTER_MAP[attribute.name]

      switch (nameFilterValue) {
        case undefined:
          break
        case 'colorMap':
          break
        case 'materials':
          fabric.filterOptions[nameLocalFilterValue] = fabrics[nameFilterValue]
            .filter((filter) =>
              attribute.options?.some(
                (option) => option.toLowerCase().indexOf(filter.name.toLowerCase()) !== -1,
              ),
            )
            // this terms are string instead of int
            ?.map((filter) => filter.terms)
            ?.flat()
          break
        default:
          fabric.filterOptions[nameLocalFilterValue] = attribute.options.map((option) =>
            parseInt(
              fabrics[nameFilterValue]?.find(
                (filterOption) => filterOption.name.toLowerCase() === option.toLowerCase(),
              )?.id,
            ),
          )
      }
    })
  })

  return fabricsRespons
}

export const applyLocalFilter = (fabrics, frontendFilters, frontendFilterKey) => {
  return fabrics.filter((fabric) =>
    fabric.filterOptions?.[frontendFilterKey]?.some((filterOption) => {
      return frontendFilters.includes(filterOption.toString())
    }),
  )
}

export const applyFavoriteFilter = (fabrics, showOnlyFavourites, localStorageFavourites) => {
  if (!showOnlyFavourites) {
    return fabrics
  }

  return fabrics.filter((fabric) => localStorageFavourites?.includes(fabric.id.toString()))
}

/**
 * Filters fabrics on frontend
 * @param frontendFilters {Object}
 * @param fabricsArray {Array}
 * @param fabrics {Object}
 * @return {Array}
 */
export const filterFabrics = (frontendFilters, fabricsArray) => {
  const localStorageFavourites = window.localStorage.getItem('favourites')
  let localFilteredFabrics = fabricsArray
  Object.keys(frontendFilters)
    .filter(
      (frontendFilterKey) =>
        frontendFilterKey !== 'showOnlyFavourites' &&
        frontendFilters[frontendFilterKey]?.length &&
        frontendFilters[frontendFilterKey][0],
    )
    .forEach((frontendFilterKey) => {
      if (
        parseInt(frontendFilters[frontendFilterKey][0], 10).toString() ===
          frontendFilters[frontendFilterKey][0] &&
        frontendFilterKey !== 'color'
      ) {
        localFilteredFabrics = applyLocalFilter(
          localFilteredFabrics,
          frontendFilters[frontendFilterKey],
          frontendFilterKey,
        )
      }
    })

  localFilteredFabrics = applyFavoriteFilter(
    localFilteredFabrics,
    frontendFilters.showOnlyFavourites,
    localStorageFavourites,
  )
  return localFilteredFabrics
}

/**
 * Counts number of fabrics per color
 * @param colorMap {Object}
 * @param fabrics {Array}
 * @return {Object}
 */
export const countFabricsPerColor = (colorMap, fabrics) => {
  let nrOfFabricsPerColor = {}
  let simpleColorMap = {}

  for (const colorKey in colorMap) {
    simpleColorMap[colorMap[colorKey].name] = colorKey
    for (const termKey in colorMap[colorKey].terms) {
      simpleColorMap[colorMap[colorKey].terms[termKey].name] = colorKey
    }
  }

  fabrics.forEach((fabric) => {
    const fabricColors = fabric.attributes.find(
      (attribute) => attribute.name.toLowerCase() === 'color',
    )?.options
    const fabricColorsMap = {}

    fabricColors?.forEach((color) => {
      let lowerCaseColor = color.toLowerCase()

      // if (lowerCaseColor.includes('blue')) {
      // 	nrOfFabricsPerColor['32'] = nrOfFabricsPerColor['32'] + 1;
      // }
      if (lowerCaseColor.includes('/multi')) {
        nrOfFabricsPerColor['62'] = nrOfFabricsPerColor['62'] + 1
        lowerCaseColor = lowerCaseColor.replace('/multi', '')
      }
      if (lowerCaseColor.includes('light/')) {
        lowerCaseColor = lowerCaseColor.replace('light/', '')
      }

      //check if a color from that family has already been counted
      if (!fabricColorsMap[simpleColorMap[lowerCaseColor]]) {
        fabricColorsMap[simpleColorMap[lowerCaseColor]] = 1

        if (nrOfFabricsPerColor[simpleColorMap[lowerCaseColor]]) {
          nrOfFabricsPerColor[simpleColorMap[lowerCaseColor]]++
        } else {
          nrOfFabricsPerColor[simpleColorMap[lowerCaseColor]] = 1
        }
      }
    })
  })

  return nrOfFabricsPerColor
}

/**
 * Scrolls to a specific Element by name
 * @param elementName {String}
 * @param duration {number}
 * @param delay {number}
 * @param offset {number}
 */
export const scrollTo = (elementName, duration = 500, delay = 0, offset = 0) => {
  scroller.scrollTo(elementName, {
    duration: duration,
    delay: delay,
    smooth: 'easeInOutQuart',
    offset: offset,
  })
}

/**
 * Returns a random number between min and max
 * @param array {Array}
 * @param order {Array}
 * @param key {string}
 * @return {Array}
 */
export const mapOrder = (array, order, key) => {
  if (!Array.isArray(array)) {
    return array
  }

  array?.sort((a, b) => {
    let A = a?.[key] || '',
      B = b?.[key] || ''

    if (order?.indexOf(A) > order?.indexOf(B)) {
      return 1
    } else {
      return -1
    }
  })

  return array
}

/**
 * Toggles current flow between custom deep seating and deep seating
 * @param dispatch {Function}
 */
export const toggleDeepSeatingView =
  (dispatch, shouldSetCurrentFlow = true) =>
  () => {
    if (shouldSetCurrentFlow) {
      dispatch(setCurrentFlow('custom-deep-seating'))
    }
    dispatch(setSelectedType(null))
    dispatch(setCanProceedToNextStep(false))
    dispatch(toggleTypesStepView())
  }

/**
 * Adds apropriate parameters to indicate fastener placement
 * @param index {Number}
 * @param placement {Number}
 * @param params {Object}
 * @param propsParams {Object}
 * @param mirroredCheckboxes {Array}
 */
export const setStandartFastenerPlacement = (
  index,
  placement,
  params,
  propsParams,
  mirroredCheckboxes,
) => {
  let isTopHalf = index <= 3
  let idx = index % 4
  propsParams.type.id === '12072' || propsParams.type.id === '166'
    ? (params[
        `attribute_pa_${!isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
      ] = placement === 1 && mirroredCheckboxes[index] === 1 ? 1 : 0)
    : (params[
        `attribute_pa_${isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
      ] = placement === 1 && mirroredCheckboxes[index] === 1 ? 1 : 0)
  if (idx === 0) {
    propsParams.type.id === '12072' || propsParams.type.id === '166'
      ? (params[
          `attribute_pa_${!isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
        ] = placement === 1 ? 1 : 0)
      : (params[
          `attribute_pa_${isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
        ] = placement === 1 ? 1 : 0)
    propsParams.type.id === '12072' || propsParams.type.id === '166'
      ? (params[
          `attribute_pa_${!isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[`${idx + 1}-mirror`]}`
        ] = mirroredCheckboxes[index] === 1 ? 1 : 0)
      : (params[
          `attribute_pa_${isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[`${idx + 1}-mirror`]}`
        ] = mirroredCheckboxes[index] === 1 ? 1 : 0)
  }
}

/**
 * Returns parameter key name for fastener placement
 * @param section {String}
 * @param placementName {String}
 * @param side {String}
 * @param type {String}
 */
export const getFastenerKeyParameter = (section, placementName, side, type) => {
  return `pa_attribute_section_${section}${placementName ? `_${placementName}` : ''}_${side}${type ? `_${type}` : ''}`
}

/**
 * Adds apropriate parameters to indicate fastener placement for chaise
 * @param index {Number}
 * @param placement {Number}
 * @param params {Object}
 * @param shapeType {String}
 */
export const setChaiseFastenerPlacement = (index, placement, params, shapeType) => {
  const section = CHAICE_FASTENER_SECTION_MAP[shapeType][index]
  const placementName = CHAICE_FASTENER_PLACEMENT_NAME_MAP[shapeType][index]
  const type = CHAICE_FASTENER_PLACEMENT_TYPE_MAP[shapeType][index]
  const fastenerKeyLeft = getFastenerKeyParameter(section, placementName, 'left', type)
  const fastenerKeyRight = getFastenerKeyParameter(section, placementName, 'right', type)
  params[fastenerKeyLeft] = placement
  params[fastenerKeyRight] = placement
}

/**
 * Estimates the price for a cushion
 * @param dispatch {Function}
 * @param general {Object}
 * @param propsParams {Object}
 */
export const getEstimate = (dispatch, general, propsParams) => () => {
  const {
    dimensions,
    type,
    shape,
    set,
    additionalShape,
    style,
    fastenerPlacement,
    zipperOrientation,
    fasteners,
    filling,
    additionalFillingOptions,
    fabric,
    cordFabric,
    isConnected,
    noOfFasteners,
  } = general
  const hasZipperOrientation = getHasZipperOrientation(general)

  const { checkboxes, mirroredCheckboxes } = fastenerPlacement

  let fabricBrand,
    fabricSku,
    fabricColor,
    fabricYard,
    cordFabricBrand,
    cordFabricSku,
    cordFabricColor,
    cordFabricYard

  if (fabric?.id !== 'own') {
    fabricBrand = fabric?.attributes?.find((attr) => attr.name === 'brand')
    fabricSku = fabric?.attributes?.find((attr) => attr.name === 'Internal Sku')
    fabricColor = fabric?.attributes?.find((attr) => attr.name === 'Internal Color Number:')
    fabricYard = fabric?.attributes?.find((attr) => attr.name === 'Ounces per Sq Yard:')
  }

  if (cordFabric?.id !== 'own') {
    cordFabricBrand = cordFabric?.attributes.find((attr) => attr.name === 'brand')
    cordFabricSku = cordFabric?.attributes.find((attr) => attr.name === 'Internal Sku')
    cordFabricColor = cordFabric?.attributes.find((attr) => attr.name === 'Internal Color Number:')
    cordFabricYard = cordFabric?.attributes.find((attr) => attr.name === 'Ounces per Sq Yard:')
  }

  const params = {}

  if (hasZipperOrientation) {
    params.pa_attribute_zipper_orientation = zipperOrientation
  }

  if (type) {
    params.product_name = type?.slug === 'custom-template' ? 'custom' : type?.slug

    if (set || propsParams?.set) {
      params.attribute_pa_set = set?.slug || propsParams?.set?.slug
    }
    if (shape || propsParams?.shape?.name) {
      params.attribute_pa_shape = shape?.slug || propsParams?.shape?.name
    }
    if (additionalShape || propsParams?.shape?.additionalShapeName) {
      params.attribute_pa_additional_shape =
        additionalShape?.slug || propsParams?.shape?.additionalShapeName
    }

    const dimensionKeys = Object.keys(dimensions)

    dimensionKeys.forEach((dimension) => {
      params[`attribute_pa_${dimension}`] = dimensions[dimension]
    })

    if (fabric || propsParams?.fabric?.name) {
      params.fabric_name =
        fabric?.id === 'own' ? fabric?.name : fabric?.slug || propsParams?.fabric?.name
      params.fabric_id = fabric?.id === 'own' ? 'cof' : fabric?.id || propsParams?.fabric?.id
    }

    if (
      fabric?.id !== 'own' &&
      fabricBrand &&
      fabricColor &&
      fabricColor &&
      fabricSku &&
      fabricYard
    ) {
      params.pa_attribute_brand = fabricBrand?.options[0]
      params.pa_attribute_internal_sku = fabricSku?.options[0]
      params.pa_attribute_internal_color_number = fabricColor?.options[0]
      params.pa_attribute_fabric_required = fabricYard?.options[0]
    }

    if (style) {
      params.attribute_pa_style = style?.slug
    }

    if (
      cordFabric?.id !== 'own' &&
      cordFabricBrand &&
      cordFabricColor &&
      cordFabricSku &&
      cordFabricSku &&
      cordFabricYard
    ) {
      params.pa_attribute_cording_brand = cordFabricBrand?.options[0]
      params.pa_attribute_cording_internal_sku = cordFabricSku?.options[0]
      params.pa_internal_color_number = cordFabricColor?.options[0]
      params.pa_attribute_cording_internal_color_number = cordFabricYard?.options[0]
    }

    if (cordFabric || propsParams?.style?.fabricName) {
      params.attribute_pa_cord_fabric =
        cordFabric?.id === 'own'
          ? cordFabric?.name
          : cordFabric?.slug || propsParams?.style?.fabricName
      params.attribute_pa_cord_fabric_name =
        cordFabric?.id === 'own'
          ? cordFabric?.name
          : cordFabric?.slug || propsParams?.style?.fabricName
      params.attribute_pa_cord_fabric_id =
        cordFabric?.id === 'own' ? 'cof' : cordFabric?.id || propsParams?.style?.fabricId
    }

    if (fasteners || propsParams?.fasteners) {
      params.fastener_id = fasteners?.id
      params.fastener_name = fasteners?.name
    }

    if (
      (checkboxes && checkboxes.length > 0) ||
      (mirroredCheckboxes && mirroredCheckboxes.length > 0)
    ) {
      if (
        propsParams.category === '28' ||
        propsParams.type.id === '11569' ||
        propsParams.type.id === '12072'
      ) {
        checkboxes.forEach((placement, index) => {
          setStandartFastenerPlacement(index, placement, params, propsParams, mirroredCheckboxes)
        })
      } else if (propsParams.type.id === '11568') {
        const shapeType = CHAICE_SHAPE_TYPES_IDS.ONE_BREAK.includes(propsParams.shape.id)
          ? 'ONE_BREAK'
          : CHAICE_SHAPE_TYPES_IDS.TWO_BREAK.includes(propsParams.shape.id)
            ? 'TWO_BREAK'
            : 'THREE_BREAK'
        checkboxes.forEach((placement, index) => {
          if (index === 0) {
            setStandartFastenerPlacement(index, placement, params, propsParams, mirroredCheckboxes)
          } else {
            setChaiseFastenerPlacement(index, placement, params, shapeType)
          }
        })
      } else {
        checkboxes.forEach((placement, index) => {
          params[`attribute_pa_${FASTENER_PLACEMENT_RECTANGLE[index + 1]}`] =
            placement === 1 && mirroredCheckboxes[index] === 1 ? '1' : '0'
          if (index + 1 === 3) {
            params[`attribute_pa_${FASTENER_PLACEMENT_RECTANGLE[index + 1]}`] =
              placement === 1 ? '1' : '0'
            params[`attribute_pa_${FASTENER_PLACEMENT_RECTANGLE[`${index + 1}-mirror`]}`] =
              mirroredCheckboxes[index] === 1 ? '1' : '0'
          }
        })
      }
    }

    if (filling) {
      params.attribute_pa_filling = filling?.slug
    }

    // check if dimensions.jointing exists and if it's emptty
    if (
      (isConnected ||
        propsParams.dimensions?.includes('join') ||
        propsParams.dimensions?.includes('pinch')) &&
      type?.id === 11786
    ) {
      params.isconnected = true
    }

    if (!isConnected) {
      // delete param isconnected
      delete params.isconnected
      if (params?.attribute_pa_jointing) {
        delete params?.attribute_pa_jointing
      }
    }

    if (additionalFillingOptions) {
      const additionalFillingOptionsKeys = Object.keys(additionalFillingOptions)
      additionalFillingOptionsKeys.forEach((additionalFillingOption, index) => {
        if (additionalFillingOptions[additionalFillingOption]) {
          params[`attribute_pa_additional_filling${index}`] =
            additionalFillingOptions[additionalFillingOption]?.slug
        }
      })
    }

    if (noOfFasteners && fasteners?.id !== 718) {
      params.custom_number_of_ties = noOfFasteners?.toString()
    }
    if (noOfFasteners && fasteners?.id === 718) {
      delete params.custom_number_of_ties
    }

    const paramsString = Object.keys(params)
      .map((key) => `${key}=${params[key]}`)
      .join('&')

    dispatch(getPrice('?' + paramsString))
  }
}

/** Check if current product has orientation for zipper
 * @param general {Object}
 */
export const getHasZipperOrientation = (general) => {
  const { category, type, shape } = general

  /** true for:
   * bench window seat
   * bench window back
   * chair with no back
   * bed cushion
   */
  return (
    shape?.slug === 'trapezoid' &&
    ((category === 769 &&
      ((type?.id === 11660 && type?.slug === 'bench-window-seat') ||
        (type?.id === 12072 && type?.slug === 'chair-with-no-back') ||
        (type?.id === 11658 && type?.slug === 'bench-window-back'))) ||
      (category === 884 && type?.id === 12107 && type?.slug === 'bed-cushion'))
  )
}

/** Check if a string is number
 * @param numberStr {String}
 */
export const isNumeric = (numberStr) => {
  return numberStr && !isNaN(numberStr)
}

/**
 * Extracts dimension from url params
 * @param cushionDimensions {Object}
 * @param params {Object}
 */
export const getDimensionsFromParams = (cushionDimensions, params) => {
  const paramsDimensions = params.dimensions.filter((dimension) => dimension !== 'dimensions')
  if (!cushionDimensions || !paramsDimensions.length) {
    return {}
  }

  const dimensionsFromParams = {}

  Object.keys(cushionDimensions).forEach((cushionDimension, index) => {
    dimensionsFromParams[cushionDimension] = params.dimensions[index]
  })

  if (params.dimensions.length > Object.keys(cushionDimensions).length) {
    dimensionsFromParams.jointing = params.dimensions.slice(-1)[0]
  }

  return dimensionsFromParams
}

// Returns the user-facing wording for a cushion shape, mirroring the card title
// For shapes 30, 787 and 788 in a multi-section
// context, returns "Outer/All [Top/Bottom/] Corners Rounded" depending on the
// individuallyRounded flag; otherwise returns the shape's name.
const getCornersWording = (shape, individuallyRounded, sectionsCount) => {
  if (!shape) return ''
  const splitShapeIds = [30, 787, 788]
  if (!splitShapeIds.includes(shape.id) || sectionsCount <= 1) {
    return shape.name || ''
  }
  const qualifier = shape.id === 30 ? '' : shape.id === 787 ? 'Top ' : 'Bottom '
  return `${individuallyRounded ? 'All' : 'Outer'} ${qualifier}Corners Rounded`
}

/**
 * Prepare cushion item object
 * @param general {Function}
 * @param history {Object}
 * @param params {Object}
 */
export const getCushionItemData = (general, history, params) => {
  const {
    type,
    shape,
    additionalShape,
    dimensions,
    fasteners,
    style,
    fabric,
    fabricDirection,
    cordFabric,
    cordFabricDirection,
    filling,
    fastenerPlacement,
    additionalFillingOptions,
    set,
    category,
    individualRoundedBack,
    individualRoundedSeat,
    isConnected,
    noOfFasteners,
    zipperOrientation,
  } = general
  const { checkboxes, mirroredCheckboxes } = fastenerPlacement
  const hasZipperOrientation = getHasZipperOrientation(general)

  let fabricBrand,
    fabricSku,
    fabricColor,
    fabricYard,
    cordFabricBrand,
    cordFabricSku,
    cordFabricColor,
    cordFabricYard,
    pa_sku,
    pa_cording_sku,
    pa_fabric_material,
    pa_cording_fabric_material

  pa_fabric_material =
    fabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'material')?.options[0] || ''
  pa_cording_fabric_material =
    cordFabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'material')?.options[0] || ''

  if (fabric?.id !== 'own') {
    fabricBrand =
      fabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'brand')?.options[0] || ''
    fabricSku =
      fabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'internal sku')?.options[0] ||
      ''
    fabricColor =
      fabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'internal color number:')
        ?.options[0] || ''
    fabricYard =
      fabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'ounces per sq yard:')
        ?.options[0] || ''
    pa_sku = fabric?.sku || ''
  }

  if (cordFabric?.id !== 'own') {
    cordFabricBrand =
      cordFabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'brand')?.options[0] || ''
    cordFabricSku =
      cordFabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'internal sku')
        ?.options[0] || ''
    cordFabricColor =
      cordFabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'internal color number:')
        ?.options[0] || ''
    cordFabricYard =
      cordFabric?.attributes?.find((attr) => attr.name.toLowerCase() === 'ounces per sq yard:')
        ?.options[0] || ''
    pa_cording_sku = cordFabric?.sku || ''
  }

  let twoDSlug = '',
    isCustom = general?.shape?.slug === 'custom'

  if ([11569, 12090, 11864, 11786, 166].includes(type?.id) && !isCustom) {
    twoDSlug = `${type.slug}-`
  }
  twoDSlug += `${isCustom ? general?.shape?.slug : general?.set?.slug || general?.shape?.slug || general?.type?.slug}${general?.set && !isCustom ? `${general?.additionalShape?.slug ? `-${general?.additionalShape?.slug}${individualRoundedSeat || individualRoundedBack ? '-individual' : ''}` : ''}-${general?.shape?.slug}` : ''}-dimensions-2d.png`

  if (general?.shape?.slug === 'trapezoid') {
    twoDSlug = `${general?.type?.slug}-${general?.shape?.slug}-dimensions-2d.png`
  }

  if (general?.type?.id === 12072) {
    if (general?.shape?.slug === 'circle' || general?.shape?.slug === 'trapezoid') {
      twoDSlug = `chair-with-no-back-${general?.shape?.slug}-dimensions-2d-1.png`
    } else {
      twoDSlug = `chair-with-no-back-${general?.shape?.slug}-dimensions-2d.png`
    }
  }

  let img_link = `${APP_URL}/wp-content/uploads/${category === 2893 && shape?.slug === 'trapezoid' ? '2022/02' : category === 27 ? '2022/01' : category === 28 && general?.type?.id === 166 && shape?.slug === 'trapezoid' ? '2022/01' : category === 769 && type?.id === 11658 && shape?.slug === 'trapezoid' ? '2022/02' : category === 884 && type?.id === 12107 && shape?.slug === 'trapezoid' ? '2022/02' : category === 769 && type?.id === 11660 && shape?.slug === 'trapezoid' ? '2022/02' : type?.id === 11864 || (type?.id === 12090 && set?.id === 875 && shape?.id === 787) ? '2022/02' : '2021/12'}/${twoDSlug}`

  const cushionItemData = {
    type: 'basic_product',
    product_name: type.slug,
    attribute_pa_shape: shape?.slug || '',
    attribute_pa_additional_shape: additionalShape?.slug || '',
    attribute_pa_style: style?.slug || '',
    attribute_pa_set: set?.slug || '',
    attribute_pa_fasteners: fasteners?.slug || '',
    attribute_pa_filling: {},
    pa_dimensions_image: img_link,
    fastener: {
      fastener_id: fasteners?.id?.toString(),
      fastener_name: fasteners?.name,
    },
  }

  if (hasZipperOrientation) {
    cushionItemData.pa_attribute_zipper_orientation = zipperOrientation
  }

  if (individualRoundedBack) {
    cushionItemData.attribute_pa_all_corrners_rounded = individualRoundedBack
  }

  if (individualRoundedSeat) {
    cushionItemData.attribute_pa_all_seat_corrners_rounded = individualRoundedSeat
  }

  const isLoveseatOrSofa = type?.id === 11864 || type?.id === 12090
  if (isLoveseatOrSofa) {
    const splitSetSlug = set?.slug?.split('-')
    const seatsCount = splitSetSlug ? parseInt(splitSetSlug[0]) : 1
    const backsCount = splitSetSlug ? parseInt(splitSetSlug[2]) : 1

    const backCornersWording = getCornersWording(shape, individualRoundedBack, backsCount)
    const seatCornersWording = getCornersWording(additionalShape, individualRoundedSeat, seatsCount)

    if (backCornersWording) {
      cushionItemData.attribute_pa_back_corrners = backCornersWording
    }

    if (seatCornersWording) {
      cushionItemData.attribute_pa_seat_corrners = seatCornersWording
    }
  }

  if (pa_sku) {
    cushionItemData.pa_sku = pa_sku
  }

  if (pa_cording_sku) {
    cushionItemData.pa_cording_sku = pa_cording_sku
  }

  if (type?.id !== 13101) {
    if (fabric?.id === 'own') {
      cushionItemData.fabric = {
        fabric_id: 'cof',
        fabric_name: 'Customer Owned Fabric',
      }
    } else {
      cushionItemData.fabric = {
        id: fabric?.id?.toString(),
        fabric_name: fabric?.name,
        fabric_direction: fabricDirection || '',
        image: fabric?.images[0]?.src,
        pa_fabric_material: pa_fabric_material,
      }
      cushionItemData.pa_attribute_brand = fabricBrand
      cushionItemData.pa_attribute_internal_sku = fabricSku
      cushionItemData.pa_attribute_internal_color_number = fabricColor
      cushionItemData.pa_attribute_fabric_required = fabricYard
    }
    if (cordFabric?.id === 'own') {
      if ([742, 790].includes(style?.id)) {
        // cord fabric id
        // cord fabric name
        cushionItemData.cord_fabric_id = 'cof'
        cushionItemData.cord_fabric_name = 'Customer Owned Fabric'
      }
    } else {
      if ([742, 790].includes(style?.id)) {
        // cord fabric id
        // cord fabric name
        cushionItemData.attribute_pa_cord_fabric = cordFabric?.slug
        cushionItemData.attribute_pa_cord_fabric_id = cordFabric?.id.toString()
        cushionItemData.attribute_pa_cord_fabric_name = cordFabric?.name
        cushionItemData.cord_fabric_image = cordFabric?.images[0]?.src
        cushionItemData.pa_attribute_cording_brand = cordFabricBrand
        cushionItemData.pa_attribute_cording_internal_sku = cordFabricSku
        cushionItemData.pa_attribute_cording_internal_color_number = cordFabricColor
        cushionItemData.pa_attribute_cording_fabric_required = cordFabricYard
        cushionItemData.pa_attribute_cording_directional = cordFabricDirection || ''
        cushionItemData.pa_cording_fabric_material = pa_cording_fabric_material
      }
    }
  }

  const dimensionKeys = Object.keys(dimensions)

  dimensionKeys.forEach((dimension) => {
    cushionItemData[`attribute_pa_${dimension}`] = dimensions[dimension]
  })

  if (!noOfFasteners) {
    // if(type?.id != 11568) {
    if (params.category === '28' || type?.id === 11569 || type?.id === 12072) {
      checkboxes.forEach((placement, index) => {
        let isTopHalf = index <= 3
        let idx = index % 4
        type?.id === 12072 || type?.id === 166
          ? (cushionItemData[
              `attribute_pa_${!isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
            ] = placement === 1 && mirroredCheckboxes[index] === 1 ? '1' : '0')
          : (cushionItemData[
              `attribute_pa_${isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
            ] = placement === 1 && mirroredCheckboxes[index] === 1 ? '1' : '0')

        if (idx === 0) {
          type?.id === 12072 || type?.id === 166
            ? (cushionItemData[
                `attribute_pa_${!isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
              ] = placement === 1 ? '1' : '0')
            : (cushionItemData[
                `attribute_pa_${isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[idx + 1]}`
              ] = placement === 1 ? '1' : '0')
          type?.id === 12072 || type?.id === 166
            ? (cushionItemData[
                `attribute_pa_${!isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[`${idx + 1}-mirror`]}`
              ] = mirroredCheckboxes[index] === 1 ? '1' : '0')
            : (cushionItemData[
                `attribute_pa_${isTopHalf ? 'top_half' : 'bottom_half'}_${FASTENER_PLACEMENT_SQUARE[`${idx + 1}-mirror`]}`
              ] = mirroredCheckboxes[index] === 1 ? '1' : '0')
        }
      })
    } else if (type?.id === 11568) {
      const shapeType = CHAICE_SHAPE_TYPES_IDS.ONE_BREAK.includes(shape?.id?.toString())
        ? 'ONE_BREAK'
        : CHAICE_SHAPE_TYPES_IDS.TWO_BREAK.includes(shape?.id?.toString())
          ? 'TWO_BREAK'
          : 'THREE_BREAK'
      checkboxes.forEach((placement, index) => {
        if (index === 0) {
          setStandartFastenerPlacement(
            index,
            placement,
            cushionItemData,
            general,
            mirroredCheckboxes,
          )
        } else {
          setChaiseFastenerPlacement(index, placement, cushionItemData, shapeType)
        }
      })
    } else {
      checkboxes.forEach((placement, index) => {
        cushionItemData[`attribute_pa_${FASTENER_PLACEMENT_RECTANGLE[index + 1]}`] =
          placement === 1 && mirroredCheckboxes[index] === 1 ? '1' : '0'
        if (index + 1 === 3) {
          cushionItemData[`attribute_pa_${FASTENER_PLACEMENT_RECTANGLE[index + 1]}`] =
            placement === 1 ? '1' : '0'
          cushionItemData[`attribute_pa_${FASTENER_PLACEMENT_RECTANGLE[`${index + 1}-mirror`]}`] =
            mirroredCheckboxes[index] === 1 ? '1' : '0'
        }
      })
    }
    // }
    // else {
    // 	checkboxes.forEach((placement, index, array) => {
    // 		let isTopHalf = index <= 2 || index === array.length - 2;
    // 		let isLastTwo = index === array.length;
    // 		let idx = index % (array.length - 1);

    // 		if (isTopHalf || isLastTwo) {
    // 			const fastener_index = index > 2 ? 3 : index;
    // 			cushionItemData[`attribute_pa_${ isTopHalf ? 'top_half' : isLastTwo ? 'bottom_half' : '' }_${ FASTENER_PLACEMENT_SQUARE[fastener_index + 1] }`] = placement === 1 && mirroredCheckboxes[index] === 1 ? '1' : '0';
    // 		}
    // 		else if( index !== array.length - 1 ) {
    // 			let section;
    // 			if (index === 3) {
    // 				section = 1;
    // 			}
    // 			else if (index > 3) {
    // 				section = Math.floor((index - 2) / 2) + 1;
    // 			}

    // 			let pos;
    // 			if(index % 2 === 0) {
    // 				pos = '_top';
    // 			}
    // 			else {
    // 				pos = '_bottom';
    // 			}

    // 			cushionItemData[`attribute_pa_fastener_section_${ section }${ pos }`] = placement === 1  && mirroredCheckboxes[index] === 1 ? '1' : '0';
    // 		}

    // 		if (idx === 0) {
    // 			cushionItemData[`attribute_pa_${ isTopHalf ? 'top_half' : 'bottom_half' }_${ FASTENER_PLACEMENT_SQUARE[idx + 1] }`] = placement === 1 ? '1' : '0';
    // 			cushionItemData[`attribute_pa_${ isTopHalf ? 'top_half' : 'bottom_half' }_${ FASTENER_PLACEMENT_SQUARE[`${ idx + 1 }-mirror`] }`] = mirroredCheckboxes[index] === 1 ? '1' : '0';
    // 		}

    // 	});
    // }
  }

  if (noOfFasteners) {
    cushionItemData.custom_number_of_ties = noOfFasteners?.toString()
  }

  // if (params?.type?.id === '11568') {
  // 	if (checkboxes[1] === 1 && mirroredCheckboxes[1] === 1) { // be careful with this
  // 		cushionItemData.attribute_pa_top_half_placement_bottom_corners = '1';
  // 		cushionItemData.attribute_pa_top_half_placement_top_corners = '1';
  // 	} else {
  // 		cushionItemData.attribute_pa_top_half_placement_bottom_corners = '0';
  // 		cushionItemData.attribute_pa_top_half_placement_top_corners = '0';
  // 	}
  // 	if (checkboxes[2] === 1 && mirroredCheckboxes[2] === 1) {
  // 		cushionItemData.attribute_pa_top_half_placement_sides_center = '1';
  // 		cushionItemData.attribute_pa_bottom_half_placement_sides_center = '1';
  // 	} else {
  // 		cushionItemData.attribute_pa_top_half_placement_sides_center = '0';
  // 		cushionItemData.attribute_pa_bottom_half_placement_sides_center = '0';
  // 	}
  // 	if (checkboxes[3] === 1 && mirroredCheckboxes[3] === 1) {
  // 		cushionItemData.attribute_pa_bottom_half_placement_top_corners = '1';
  // 		cushionItemData.attribute_pa_bottom_half_placement_bottom_corners = '1';
  // 	} else {
  // 		cushionItemData.attribute_pa_bottom_half_placement_top_corners = '0';
  // 		cushionItemData.attribute_pa_bottom_half_placement_bottom_corners = '0';
  // 	}
  // 	if (checkboxes[4] === 1 && mirroredCheckboxes[4] === 1) {
  // 		cushionItemData.attribute_pa_bottom_half_placement_top_center = '1';
  // 		cushionItemData.attribute_pa_bottom_half_placement_bottom_center = '1';
  // 	} else {
  // 		cushionItemData.attribute_pa_bottom_half_placement_top_center = '0';
  // 		cushionItemData.attribute_pa_bottom_half_placement_bottom_center = '0';
  // 	}
  // 	if (mirroredCheckboxes[0] === 1) {
  // 		cushionItemData.attribute_pa_top_half_placement_top_center = '1';
  // 	} else {
  // 		cushionItemData.attribute_pa_top_half_placement_top_center = '0';
  // 	}
  // 	if (checkboxes[0] === 1) {
  // 		cushionItemData.attribute_pa_top_half_placement_bottom_center = '1';
  // 	} else {
  // 		cushionItemData.attribute_pa_top_half_placement_bottom_center = '0';
  // 	}
  // }

  // check if dimensions.jointing exists and if it's emptty
  if (isConnected && type?.id === 11786) {
    cushionItemData.isconnected = true
  }

  if (!isConnected) {
    // delete param isconnected
    delete cushionItemData.isconnected
    if (cushionItemData?.attribute_pa_jointing) {
      delete cushionItemData?.attribute_pa_jointing
    }
  }

  Object.keys(additionalFillingOptions).map((key) => {
    if (additionalFillingOptions[key]) {
      cushionItemData.attribute_pa_filling[key] = additionalFillingOptions[key].slug
    }
  })

  cushionItemData.attribute_pa_filling[filling.id] = filling.slug

  let additionalFillingOptionURL = `${additionalFillingOptions['726'] ? '726' : ''}`
  let polyBunFillingOptionURL = additionalFillingOptions['874'] ? '-874' : ''

  let product_edit_link = `${APP_URL}${
    history.location.pathname.includes('/filling')
      ? history.location.pathname.replace(
          '/filling',
          `/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`,
        )
      : history.location.pathname.includes('/722-foam-filling-material-')
        ? `${history.location.pathname.replace('/722-foam-filling-material-', '')}/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`
        : history.location.pathname.includes('/723-densified-fiber-filling-material-')
          ? `${history.location.pathname.replace('/723-densified-fiber-filling-material-', '')}/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`
          : history.location.pathname.includes('/724-reticulated-')
            ? `${history.location.pathname.replace('/724-reticulated-', '')}/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`
            : history.location.pathname.includes('/725-cushion-cover-only-')
              ? `${history.location.pathname.replace('/725-cushion-cover-only-', '')}/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`
              : history.location.pathname.includes('/874-poly-bun-')
                ? `${history.location.pathname.replace('/874-poly-bun-', '')}/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`
                : history.location.pathname.includes('/873-direct-blown-')
                  ? `${history.location.pathname.replace('/873-direct-blown-', '')}/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`
                  : `${history.location.pathname}/${filling?.id}-${filling?.slug}-${additionalFillingOptionURL}${polyBunFillingOptionURL}`
  }`

  if (!isConnected && type?.id === 11786) {
    if (product_edit_link.includes('-join')) {
      product_edit_link = product_edit_link.replace('-join', '')
    }
    if (product_edit_link.includes('-pinch')) {
      product_edit_link = product_edit_link.replace('-pinch', '')
    }
  }

  cushionItemData.product_edit_link = product_edit_link

  return cushionItemData
}

export const sendGtag = (eventName = '', ...args) => {
  // eslint-disable-next-line no-console
  console.log('sendGtag try', eventName, ...args)
  if (window?.sendReactGtag) {
    // eslint-disable-next-line no-console
    console.log('sendGtag try success')
    window.sendReactGtag(eventName, ...args)
  }
}
